import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import usePaymentStatus from "../../hooks/usePaymentStatus";

/* ==========================================================================
   CONSTANTS / CONFIG
========================================================================== */

const PERMISSION_OPTIONS = [ "Family", "Friend", "Work", "Others"];

const DISPLAY_TO_API_KEY = {
  Others: "Public", // UI 'Others' maps to backend 'Public'
  Friend: "Friend",
  Family: "Family",
  Work: "Work",
};

const ENDPOINTS = {
  "My Profile": "/myprofile-profilepermission/get",
  "Personal Info": "/personal-profilepermission/get",
  Address: "/home-address-profilepermission/get",
  Education: "/education-profilepermission/get",
  "Work Experience": "/work-experience-profilepermission/get",
};

const RESPONSE_KEYS = {
  "My Profile": "myprofile_profilepermission",
  "Personal Info": "personal_profilepermission",
  Address: "home_address_profilepermission",
  Education: "education_profilepermission",
  "Work Experience": "work_experience_profilepermission",
};

/* ==========================================================================
   SECTION FIELD DEFS  (minPlan: 1=Basic, 2=Basic Plus, 3=Pro)
========================================================================== */

// Personal Info: plan 1 = basic info; plan 2+ = address fields; plan 3 = map/building
// Work Experience: plan 1 = Not Applicable entirely; plan 2+ = core; plan 3 = building/map
// Education: plan 1 = core; plan 2+ = level/website; plan 3 = building/map/duration

const HOME_ADDRESS_FIELDS = [
  { key: "flat_number",          label: "Flat Number / House Number", minPlan: 1, group: "Address Permission" },
  { key: "building_name",        label: "Building Name / House Name", minPlan: 1, group: "Address Permission" },
  { key: "address_google_map",   label: "Address",                     minPlan: 1, group: "Address Permission" },
  // { key: "latitude",             label: "Latitude",                    minPlan: 2, group: "Address Permission" },
  // { key: "longitude",            label: "Longitude",                   minPlan: 2, group: "Address Permission" },
  // { key: "street_name",          label: "Street Name",                 minPlan: 2, group: "Address Permission" },
  // { key: "area_locality",        label: "Area / Locality",             minPlan: 2, group: "Address Permission" },
  // { key: "home_address_city",    label: "City",                        minPlan: 2, group: "Address Permission" },
  // { key: "home_address_region",  label: "Region",                      minPlan: 2, group: "Address Permission" },
  // { key: "home_address_country", label: "Country",                     minPlan: 2, group: "Address Permission" },
  // { key: "home_address_pincode", label: "Pincode - Home",              minPlan: 2, group: "Address Permission" },
];

const HOME_ADDRESS_FIELD_KEYS = new Set(HOME_ADDRESS_FIELDS.map((f) => f.key));

const SECTION_FIELD_DEFS = {
  "Personal Info": [
    { key: "first_name",                 label: "First Name",                      minPlan: 1, group: "Basic Permission" },
    { key: "last_name",                  label: "Last Name",                       minPlan: 1, group: "Basic Permission" },
    // { key: "voice_full_name",            label: "Full Name (Voice)",               minPlan: 1, group: "Basic Permission" },
    { key: "personal_email_id",          label: "Personal Email ID",               minPlan: 1, group: "Basic Permission" },
    { key: "work_email_id",              label: "Work Email ID",                   minPlan: 1, group: "Basic Permission" },
    { key: "personal_mobile",            label: "Personal Mobile",                 minPlan: 1, group: "Basic Permission" },
    { key: "work_mobile",                label: "Work Mobile",                     minPlan: 1, group: "Basic Permission" },
    { key: "date_of_birth_DD_MM",        label: "Date of Birth (DD/MM)",           minPlan: 1, group: "Basic Permission" },
    { key: "date_of_birth_YYYY",         label: "Date of Birth (YYYY)",            minPlan: 1, group: "Basic Permission" },
    { key: "marital_status",             label: "Marital Status",                  minPlan: 1, group: "Basic Permission" },
    { key: "date_of_anniversary",        label: "Date of Anniversary",             minPlan: 1, group: "Basic Permission" },
    { key: "birth_city_country",         label: "Birth City and Country",          minPlan: 2, group: "Basic Permission" },
    // { key: "basic_pincode_home",         label: "Pincode - Home",                  minPlan: 2, group: "Basic Permission" },
    { key: "current_city_region_country",label: "Current City, Region and Country",minPlan: 2, group: "Basic Permission" },
    { key: "secondary_email",            label: "Secondary Email",                 minPlan: 2, group: "Basic Permission" },
    { key: "secondary_mobile",           label: "Secondary Mobile",                minPlan: 2, group: "Basic Permission" },
    { key: "home_fixed",                 label: "Home Fixed",                      minPlan: 2, group: "Basic Permission" },
    { key: "work_fixed",                 label: "Work Fixed",                      minPlan: 2, group: "Basic Permission" },
    ...HOME_ADDRESS_FIELDS,
  ],

  "Work Experience": [
    { key: "work_current_job",              label: "Current Job",                    minPlan: 2, group: "Work Experience" },
    { key: "company_name",                  label: "Company Name",                   minPlan: 2, group: "Work Experience" },
    // { key: "work_experience_pin_code",      label: "Pincode",                        minPlan: 2, group: "Basic Permission" },
    { key: "city_region_country",           label: "City, Region and Country",       minPlan: 2, group: "Work Experience" },
    { key: "job_title",                     label: "Job Title",                      minPlan: 2, group: "Work Experience" },
    { key: "work_start_date",               label: "Start Date",                     minPlan: 2, group: "Work Experience" },
    { key: "work_end_date",                 label: "End Date",                       minPlan: 2, group: "Work Experience" },
    { key: "job_function",                  label: "Job Function",                   minPlan: 2, group: "Work Experience" },
    { key: "job_sub_functions",             label: "Job Sub-Function",               minPlan: 2, group: "Work Experience" },
    { key: "job_description",               label: "Description",                    minPlan: 2, group: "Work Experience" },

    { key: "floor_and_office_number",       label: "Building Number / Office Number", minPlan: 3, group: "Work Address" },
    { key: "building_name",                 label: "Building Name",                  minPlan: 3, group: "Work Address" },
    { key: "work_address_google_map",       label: "Address",                        minPlan: 3, group: "Work Address" },
    // { key: "work_address_latitude",         label: "Latitude",                       minPlan: 2, group: "Work Address" },
    // { key: "work_address_longitude",        label: "Longitude",                      minPlan: 2, group: "Work Address" },
    // { key: "work_address_street_name",      label: "Street Name",                    minPlan: 2, group: "Work Address" },
    // { key: "work_address_area_locality",    label: "Area / Locality",                minPlan: 2, group: "Work Address" },
    // { key: "work_address_pin_code",         label: "Pincode",                        minPlan: 2, group: "Work Address" },
    // { key: "work_address_city",             label: "City",                           minPlan: 2, group: "Work Address" },
    // { key: "work_address_region",           label: "Region",                         minPlan: 2, group: "Work Address" },
    // { key: "work_address_country",          label: "Country",                        minPlan: 2, group: "Work Address" },
    { key: "company_website",               label: "Website",                        minPlan: 2, group: "Work Address" },
  ],

  Address: [...HOME_ADDRESS_FIELDS],

  Education: [
    { key: "education_current_studying",  label: "Currently Studying",              minPlan: 1, group: "Institution and Course Details" },
    { key: "edu_institution_name",            label: "Name of the Institution",         minPlan: 1, group: "Institution and Course Details" },
    // { key: "edu_pin_code",                label: "Pincode",                         minPlan: 1, group: "Basic Permission" },
    { key: "edu_city_region_country",     label: "City, Region and Country",        minPlan: 1, group: "Institution and Course Details" },
    { key: "education_level",             label: "Education Level",                 minPlan: 1, group: "Institution and Course Details" },
    { key: "course_name",                 label: "Course Name",                     minPlan: 1, group: "Institution and Course Details" },
    { key: "edu_start_date",              label: "Start Date",                      minPlan: 1, group: "Institution and Course Details" },
    { key: "edu_end_date",                label: "End Date",                        minPlan: 1, group: "Institution and Course Details" },
    { key: "edu_duration_of_program",     label: "Duration of Program",             minPlan: 3, group: "Institution and Course Details" },
    { key: "edu_program_delivery",        label: "Program Delivery",                minPlan: 3, group: "Institution and Course Details" },

    { key: "edu_address_building_number", label: "Building Number / Office Number", minPlan: 3, group: "Institution Address" },
    { key: "edu_address_building_name",   label: "Building Name",                   minPlan: 3, group: "Institution Address" },
    { key: "edu_address_google_map",      label: "Address",                         minPlan: 3, group: "Institution Address" },
    // { key: "edu_address_latitude",        label: "Latitude",                        minPlan: 2, group: "Institution Address" },
    // { key: "edu_address_longitude",       label: "Longitude",                       minPlan: 2, group: "Institution Address" },
    // { key: "edu_address_street_name",     label: "Street Name",                     minPlan: 2, group: "Institution Address" },
    // { key: "edu_address_area_locality",   label: "Area / Locality",                 minPlan: 2, group: "Institution Address" },
    // { key: "edu_address_pincode",         label: "Pincode",                         minPlan: 2, group: "Institution Address" },
    // { key: "edu_address_city",            label: "City",                            minPlan: 2, group: "Institution Address" },
    // { key: "edu_address_region",          label: "Region",                          minPlan: 2, group: "Institution Address" },
    // { key: "edu_address_country",         label: "Country",                         minPlan: 2, group: "Institution Address" },
    { key: "edu_address_website",         label: "Website",                         minPlan: 2, group: "Institution Address" },
  ],
};

/* ==========================================================================
   HELPERS
========================================================================== */

const getNested = (obj, path) =>
  path.split(".").reduce((acc, key) => acc?.[key], obj);

export { getNested };

export const getRelationPermissions = (permissions, displayKey) => {
  if (displayKey !== "Others") return permissions?.[displayKey] || {};
  return permissions?.Other || permissions?.Public || {};
};

export const getRelationKeyForToggle = (permissions, displayKey) => {
  if (displayKey !== "Others") return displayKey;
  if (permissions?.Other) return "Other";
  if (permissions?.Public) return "Public";
  return "Other";
};

export const isFieldReadOnly = (value) => Number(value) === 2;
export const isFieldSelected = (value) => {
  const normalized = Number(value);
  return normalized === 1 || normalized === 2;
};

const setNested = (obj, path, value) => {
  const keys = path.split(".");
  let cur = obj;
  keys.slice(0, -1).forEach((k) => {
    if (!cur[k]) cur[k] = {};
    cur = cur[k];
  });
  cur[keys[keys.length - 1]] = value;
};

const relationToDisplayKey = (relation) =>
  relation === "Other" || relation === "Public" ? "Others" : relation;

/* ==========================================================================
   UI COMPONENTS
========================================================================== */

const RadioButton = React.memo(({ checked, onClick, disabled = false }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    onClick();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-5 h-5 md:ml-5 rounded-full border flex items-center justify-center transition-colors ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      } ${
        checked ? "border-[#6938EF] bg-purple-100" : "border-gray-300 hover:border-gray-400"
      }`}
      disabled={disabled}
    >
      {checked && (
        <img
          src="../chatscreen/radiobutton.svg"
          alt="selected"
          className="w-full h-full"
        />
      )}
    </button>
  );
});

const MobileCard = React.memo(
  ({ activeTab, label, field, permissionsForSection, onToggle }) => (
    <div className="bg-white p-4 rounded-lg border border-gray-200">
      <div className="text-sm font-medium text-gray-700 mb-3">{label}</div>
      <div className="flex flex-col justify-between items-center w-full">
        <div className="flex space-x-4 text-xs text-gray-400">
          {PERMISSION_OPTIONS.map((d) => (
            <div key={d} className="min-w-[36px] text-left ml-3">
              {d[0]}
            </div>
          ))}
        </div>
        <div className="flex space-x-4 mt-2">
          {PERMISSION_OPTIONS.map((p) => {
            const relation = getRelationKeyForToggle(permissionsForSection, p);
            const relationPermissions = getRelationPermissions(permissionsForSection, p);
            const value = Number(getNested(relationPermissions, field) ?? 0);
            const checked = isFieldSelected(value);
            const disabled = isFieldReadOnly(value);
            return (
              <div key={p} className="min-w-[36px]">
                <RadioButton
                  checked={checked}
                  disabled={disabled}
                  onClick={() => !disabled && onToggle(relation, field)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  )
);

const DesktopTable = React.memo(
  ({ activeTab, rows, permissions, onToggle }) => (
    <div className="hidden md:block">
      <table className="w-full">
        <thead>
          <tr className="border-b">
            <th className="text-center py-2"></th>
            {PERMISSION_OPTIONS.map((p) => (
                <th key={p} className="text-left py-2">
                {p}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (row.type === "header") {
              return (
                <tr key={`header-${row.title}`}>
                  <td colSpan={PERMISSION_OPTIONS.length + 1} className="py-4 px-4">
                    <div className="inline-flex items-center justify-center bg-white rounded-full px-3 py-1 text-md  font-semibold shadow-[0_8px_20px_rgba(0,0,0,0.10)]">
                      {row.title}
                    </div>
                  </td>
                </tr>
              );
            }

            return (
              <tr key={row.field} className="border-b hover:bg-gray-50">
                <td className="py-3 pl-4 font-medium max-w-[240px] text-[#616161]">
                  {row.label}
                </td>
                {PERMISSION_OPTIONS.map((p) => {
                  const relation = getRelationKeyForToggle(permissions, p);
                  const relationPermissions = getRelationPermissions(permissions, p);
                  const value = Number(getNested(relationPermissions, row.field) ?? 0);
                  const checked = isFieldSelected(value);
                  const disabled = isFieldReadOnly(value);
                  return (
                    <td key={p} className="text-center py-3">
                      <RadioButton
                        checked={checked}
                        disabled={disabled}
                        onClick={() => !disabled && onToggle(relation, row.field)}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  )
);

const MobileCards = React.memo(
  ({ activeTab, rows, permissions, onToggle }) => (
    <div className="md:hidden space-y-3">
      {rows.map((row) => {
        return (
          <React.Fragment key={row.type === "header" ? `header-${row.title}` : row.field}>
            {row.type === "header" && (
              <div className="py-2 flex justify-center">
                <div className="inline-flex items-center justify-center bg-white rounded-full px-8 py-2.5 md:text-2xl font-extrabold md:text-black shadow-[0_8px_20px_rgba(0,0,0,0.14)]">
                  {row.title}
                </div>
              </div>
            )}

            {row.type === "field" && (
              <MobileCard
                activeTab={activeTab}
                label={row.label}
                field={row.field}
                permissionsForSection={permissions}
                onToggle={onToggle}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  )
);
/* ==========================================================================
   MAIN PAGE
========================================================================== */

export const PermissionPage = ({ activeTab = "Personal Info" }) => {
  const [permissions, setPermissions] = useState({});
  const token = useRef(Cookies.get("auth_token")).current;
  const { planType, loading: paymentStatusLoading } = usePaymentStatus();

  // Filter fields by planType — only show rows applicable to the user's plan
  const section = useMemo(() => {
    const defs = SECTION_FIELD_DEFS[activeTab];
    if (!defs) return null;
    const filtered = defs.filter((d) => planType >= d.minPlan);
    const groups = [];
    const seen = new Set();

    filtered.forEach((d) => {
      const group = d.group || "Basic Permission";
      if (!seen.has(group)) {
        seen.add(group);
        groups.push(group);
      }
    });

    const rows = [];
    groups.forEach((group) => {
      rows.push({ type: "header", title: group });
      filtered
        .filter((d) => (d.group || "Basic Permission") === group)
        .forEach((d) => {
          rows.push({
            type: "field",
            field: d.key,
            label: d.label,
          });
        });
    });

    return {
      fields: filtered.map((d) => d.key),
      labels: filtered.map((d) => d.label),
      rows,
    };
  }, [activeTab, planType]);

  /* ================= FETCH ================= */

  const fetchPermissions = useCallback(async () => {
    try {
      const endpoint = ENDPOINTS[activeTab];
      if (!endpoint) return;

      // Personal Info screen includes Home Address fields too,
      // so fetch both permission sources and merge by relation.
      if (activeTab === "Personal Info") {
        const [personalRes, homeAddressRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}${ENDPOINTS["Personal Info"]}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}${ENDPOINTS.Address}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const personalData = await personalRes.json();
        const homeAddressData = await homeAddressRes.json();

        if (personalData?.status !== "1" || homeAddressData?.status !== "1") {
          throw new Error("Failed to load permissions");
        }

        const personalWrapper = personalData.data?.[0] || {};
        const homeWrapper = homeAddressData.data?.[0] || {};

        const personalPermissions =
          personalWrapper[RESPONSE_KEYS["Personal Info"]] ||
          personalWrapper[Object.keys(personalWrapper)[0]] ||
          {};

        const homeAddressPermissions =
          homeWrapper[RESPONSE_KEYS.Address] ||
          homeWrapper[Object.keys(homeWrapper)[0]] ||
          {};

        const relations = new Set([
          ...Object.keys(personalPermissions),
          ...Object.keys(homeAddressPermissions),
        ]);

        const merged = {};
        relations.forEach((relation) => {
          merged[relation] = {
            ...(personalPermissions[relation] || {}),
            ...(homeAddressPermissions[relation] || {}),
          };
        });

        setPermissions(merged);
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();

      if (data?.status === "1") {
        const wrapper = data.data?.[0] || {};
        const responseKey = RESPONSE_KEYS[activeTab];
        setPermissions(wrapper[responseKey] || wrapper[Object.keys(wrapper)[0]] || {});
      } else {
        toast.error(data.message || "Failed to load permissions");
      }
    } catch {
      toast.error("Failed to load permissions");
    }
  }, [activeTab]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /* ================= TOGGLE ================= */

  const handleToggle = useCallback(
    async (relation, fieldPath) => {
      const endpoint =
        activeTab === "Personal Info" && HOME_ADDRESS_FIELD_KEYS.has(fieldPath)
          ? ENDPOINTS.Address?.replace("/get", "/set")
          : ENDPOINTS[activeTab]?.replace("/get", "/set");
      if (!endpoint) return;
      // Get current value from backend state map
      const currentValue = getNested(permissions?.[relation], fieldPath);
      const current = Number(currentValue) || 0;

      // 2 means read-only from backend; do not allow toggling
      if (isFieldReadOnly(current)) return;

      const next = current === 1 ? 0 : 1; // Toggle: 1 -> 0, 0 -> 1

      // Update UI optimistically
      setPermissions((prev) => {
        const copy = structuredClone(prev);
        if (!copy[relation]) copy[relation] = {};
        setNested(copy[relation], fieldPath, next);
        return copy;
      });

      const formData = new FormData();
      formData.append("relation", relation);
      formData.append(fieldPath.replace(/\./g, "_"), next);

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          }
        );
        const data = await res.json();
        if (data?.status !== "1") throw new Error();
        toast.dismiss();
        toast.success("Permission updated");
      } catch (error) {
        toast.error("Update failed");
        // Revert on error
        fetchPermissions();
      }
    },
    [activeTab, permissions, fetchPermissions]
  );

  /* ================= NO PLAN SCREEN ================= */

  if (paymentStatusLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5AF8]" />
      </div>
    );
  }

  if (!planType) return (
    <div className="w-full flex items-center justify-center h-[90vh] p-6">
      <div className="max-w-5xl w-full bg-white rounded-2xl p-8 shadow-lg grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        {/* Left column: text, perks, CTA */}
        <div className="px-2 md:px-6">
          <div className="w-20 h-20 rounded-full bg-[#F6F4FF] flex items-center justify-center mb-4">
            <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="g1a" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#BFA7FF"/>
                  <stop offset="100%" stopColor="#7B4DFF"/>
                </linearGradient>
              </defs>
              <circle cx="40" cy="55" r="15" fill="url(#g1a)"/>
              <circle cx="80" cy="55" r="15" fill="url(#g1a)"/>
            </svg>
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-2">No Active Plan</h3>
          <p className="text-gray-600 mb-6">You don't have an active plan yet. Choose a plan to unlock all the features and make the most of  PhoneBookPlus.</p>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#BFA7FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <circle cx="40" cy="55" r="15" fill="url(#icon1)"/>
                <circle cx="80" cy="55" r="15" fill="url(#icon1)"/>
                <path d="M35 25C50 10 70 10 85 25" stroke="#8D63FF" strokeWidth="6" strokeLinecap="round"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Build stronger connections</p>
            </div>

            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon2" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#CDB7FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <path d="M60 10L100 25V55C100 82 82 102 60 110C38 102 20 82 20 55V25L60 10Z" fill="url(#icon2)"/>
                <rect x="42" y="50" width="36" height="28" rx="6" fill="#8D63FF"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Keep your data secure</p>
            </div>

            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon3" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#D5C4FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <rect x="20" y="65" width="18" height="35" rx="4" fill="url(#icon3)"/>
                <rect x="48" y="50" width="18" height="50" rx="4" fill="url(#icon3)"/>
                <rect x="76" y="35" width="18" height="65" rx="4" fill="url(#icon3)"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Boost productivity and grow</p>
            </div>
          </div>

          <button onClick={() => window.location.href = "/payment"} className="mt-2 inline-flex items-center gap-3 bg-[#6D5EF7] text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:bg-[#5b49e8] transition">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Choose a Plan
          </button>
        </div>

        {/* Right column: illustration */}
        <div className="flex items-center justify-center p-4">
          <svg width="320" height="240" viewBox="0 0 320 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-w-full h-auto">
            <defs>
              <linearGradient id="illGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#F3EEFF"/>
                <stop offset="100%" stopColor="#F8F6FF"/>
              </linearGradient>
            </defs>
            <rect x="20" y="20" width="220" height="200" rx="16" fill="url(#illGrad)" stroke="#EDE7FF" strokeWidth="2"/>
            <rect x="52" y="44" width="156" height="14" rx="7" fill="#FFFFFF"/>
            <rect x="52" y="72" width="156" height="12" rx="6" fill="#EDE7FF"/>
            <rect x="52" y="96" width="120" height="12" rx="6" fill="#EDE7FF"/>
            <circle cx="260" cy="40" r="28" fill="#F6EEFF"/>
          </svg>
        </div>
      </div>
    </div>
  );

  if (!section) return null;

  /* ================= RENDER ================= */

  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* HEADER */}
      <div className="p-4 md:p-6 flex-shrink-0">
        <h2 className="text-xl font-semibold">
          Permission – {activeTab}
        </h2>
      </div>

      {/* SCROLL AREA (ONLY ONE) */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 pb-6 max-md:pb-[calc(1.5rem+var(--mobile-nav-offset,5rem))] [&::-webkit-scrollbar]:hidden">
        <DesktopTable
          activeTab={activeTab}
          rows={section.rows}
          permissions={permissions}
          onToggle={handleToggle}
        />
          <MobileCards
          activeTab={activeTab}
          rows={section.rows}
          permissions={permissions}
          onToggle={handleToggle}
        />
      </div>
    </div>
  );
};
